import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import hljs from 'highlight.js';
import {
  GitBranch,
  GitCommit,
  GitPullRequest,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  X,
  FileCode,
  Copy,
  Check,
  AlertTriangle,
  Database,
} from 'lucide-react';
import {
  type AIEditBlock,
  type PushMode,
  type PushBranchOptions,
  generateBranchName,
  detectCommitType,
  pushAIEditToGitHub,
} from '@/services/repoEditService';
import type { RepoPushRecord } from '@/services/repoPushStateStore';
import { reindexFilesFromContent, type ReindexFilesResult } from '@/services/repoRagService';
import { listBranches, getRepoInfo, type GitHubBranch } from '@/services/githubService';

const EXT_TO_LANG: Record<string, string> = {
  ts: 'typescript', tsx: 'typescript',
  js: 'javascript', jsx: 'javascript',
  py: 'python', rb: 'ruby',
  go: 'go', rs: 'rust',
  java: 'java', kt: 'kotlin',
  css: 'css', scss: 'scss',
  html: 'html', xml: 'xml',
  json: 'json', yaml: 'yaml', yml: 'yaml',
  sh: 'bash', bash: 'bash',
  sql: 'sql', md: 'markdown',
};

function getLanguage(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  return EXT_TO_LANG[ext] || 'plaintext';
}

function isValidBranchName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && !/\s/.test(trimmed);
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  const ref = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.removeAttribute('data-highlighted');
    ref.current.className = `language-${language}`;
    hljs.highlightElement(ref.current);
  }, [code, language]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  }, [code]);

  return (
    <div className="relative group rounded-lg overflow-hidden border border-gray-700/60 bg-[#0d1117]">
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-gray-700/60">
        <span className="text-xs text-gray-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="overflow-x-auto">
        <pre className="p-4 text-xs leading-relaxed m-0 bg-transparent">
          <code ref={ref} className={`language-${language} hljs`}>
            {code}
          </code>
        </pre>
      </div>
    </div>
  );
}

type Step = 'branch' | 'commit' | 'pr' | 'rag';
type Status = 'idle' | 'loading' | 'success' | 'error';

const PUSH_MODE_LABELS: Record<PushMode, string> = {
  new_branch_pr: 'New branch + PR',
  existing_branch: 'Commit to existing branch',
  commit_only: 'Commit only',
};

interface Props {
  editBlock: AIEditBlock;
  owner: string;
  repo: string;
  defaultBranch?: string;
  pushRecord?: RepoPushRecord;
  onDismiss: () => void;
  onPushed?: (record: RepoPushRecord) => void;
  onRagFilesUpdated?: (files: Array<{ file_path: string; content: string }>) => void;
}

export default function RepoDiffPanel({
  editBlock,
  owner,
  repo,
  defaultBranch: defaultBranchProp,
  pushRecord,
  onDismiss,
  onPushed,
  onRagFilesUpdated,
}: Props) {
  const suggestedBranch = generateBranchName(
    editBlock.files[0]?.description || 'ai-changes',
    detectCommitType(editBlock.files)
  );

  const [pushMode, setPushMode] = useState<PushMode>('new_branch_pr');
  const [branchName, setBranchName] = useState(suggestedBranch);
  const [baseBranch, setBaseBranch] = useState('');
  const [prBase, setPrBase] = useState('');
  const [existingHeadBranch, setExistingHeadBranch] = useState('');
  const [openPr, setOpenPr] = useState(true);
  const [commitOnlyNewBranch, setCommitOnlyNewBranch] = useState(false);

  const [branches, setBranches] = useState<GitHubBranch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [branchesError, setBranchesError] = useState<string | null>(null);
  const [resolvedDefaultBranch, setResolvedDefaultBranch] = useState(defaultBranchProp ?? 'main');

  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(() => new Set([editBlock.files[0]?.file_path]));
  const [status, setStatus] = useState<Status>(pushRecord ? 'success' : 'idle');
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prUrl, setPrUrl] = useState<string | null>(pushRecord?.prUrl ?? null);
  const [committedBranch, setCommittedBranch] = useState<string | null>(pushRecord?.branch ?? null);
  const [ragResult, setRagResult] = useState<ReindexFilesResult | null>(
    pushRecord?.ragUpdated
      ? { chunksIndexed: 1, symbolsStored: 0, paths: pushRecord.files }
      : null
  );
  const [ragWarning, setRagWarning] = useState<string | null>(null);

  const isPushed = !!pushRecord || status === 'success';

  useEffect(() => {
    if (pushRecord) return;
    let cancelled = false;
    (async () => {
      setBranchesLoading(true);
      setBranchesError(null);
      try {
        const [branchList, info] = await Promise.all([
          listBranches(owner, repo),
          defaultBranchProp ? Promise.resolve(null) : getRepoInfo(owner, repo).catch(() => null),
        ]);
        if (cancelled) return;
        const def = defaultBranchProp ?? info?.default_branch ?? 'main';
        setResolvedDefaultBranch(def);
        setBranches(branchList);
        setBaseBranch(def);
        setPrBase(def);
        if (branchList.length > 0) {
          const names = branchList.map(b => b.name);
          setExistingHeadBranch(names.includes(def) ? def : branchList[0].name);
        }
      } catch (err) {
        if (!cancelled) {
          setBranchesError(err instanceof Error ? err.message : 'Failed to load branches');
        }
      } finally {
        if (!cancelled) setBranchesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, repo, defaultBranchProp, pushRecord]);

  const branchNames = useMemo(() => branches.map(b => b.name), [branches]);

  const willCreateBranch =
    pushMode === 'new_branch_pr' ||
    (pushMode === 'commit_only' && commitOnlyNewBranch);

  const willOpenPr =
    pushMode === 'new_branch_pr' ||
    (pushMode === 'existing_branch' && openPr && existingHeadBranch !== prBase);

  const activeSteps = useMemo((): Step[] => {
    const steps: Step[] = [];
    if (willCreateBranch) steps.push('branch');
    steps.push('commit');
    if (willOpenPr) steps.push('pr');
    steps.push('rag');
    return steps;
  }, [willCreateBranch, willOpenPr]);

  const headBranch = useMemo(() => {
    if (pushMode === 'new_branch_pr') return branchName.trim();
    if (pushMode === 'commit_only' && commitOnlyNewBranch) return branchName.trim();
    return existingHeadBranch;
  }, [pushMode, branchName, commitOnlyNewBranch, existingHeadBranch]);

  const canSubmit = useMemo(() => {
    if (branchesLoading) return false;
    if (pushMode === 'new_branch_pr' || (pushMode === 'commit_only' && commitOnlyNewBranch)) {
      return isValidBranchName(branchName) && baseBranch.length > 0;
    }
    if (pushMode === 'existing_branch' || (pushMode === 'commit_only' && !commitOnlyNewBranch)) {
      return existingHeadBranch.length > 0;
    }
    return false;
  }, [branchesLoading, pushMode, branchName, baseBranch, existingHeadBranch, commitOnlyNewBranch]);

  const primaryButtonLabel = useMemo(() => {
    if (pushMode === 'new_branch_pr') return 'Commit & Open PR';
    if (pushMode === 'existing_branch') return openPr ? 'Commit & Open PR' : 'Commit to Branch';
    return 'Commit Only';
  }, [pushMode, openPr]);

  const toggleFile = useCallback((path: string) => {
    setExpandedFiles(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  }, []);

  const buildBranchOptions = useCallback((): PushBranchOptions => {
    if (pushMode === 'new_branch_pr') {
      return {
        mode: 'new_branch_pr',
        headBranch: branchName.trim(),
        baseBranch,
        prBase,
      };
    }
    if (pushMode === 'existing_branch') {
      return {
        mode: 'existing_branch',
        headBranch: existingHeadBranch,
        prBase,
        openPr,
      };
    }
    if (commitOnlyNewBranch) {
      return {
        mode: 'commit_only',
        headBranch: branchName.trim(),
        baseBranch,
      };
    }
    return {
      mode: 'commit_only',
      headBranch: existingHeadBranch,
    };
  }, [pushMode, branchName, baseBranch, prBase, existingHeadBranch, openPr, commitOnlyNewBranch]);

  const handleCommit = useCallback(async () => {
    if (!canSubmit) return;
    setStatus('loading');
    setError(null);
    setRagWarning(null);
    setRagResult(null);
    setPrUrl(null);
    setCommittedBranch(null);

    const branchOptions = buildBranchOptions();
    const showBranchStep = willCreateBranch;
    if (showBranchStep) setCurrentStep('branch');

    try {
      const result = await pushAIEditToGitHub(owner, repo, editBlock, branchOptions, (step) => {
        setCurrentStep(step);
      });

      setCommittedBranch(result.branch);
      if (result.pr) setPrUrl(result.pr.prUrl);

      setCurrentStep('rag');
      const files = editBlock.files.map(f => ({ file_path: f.file_path, content: f.content }));
      let rag: ReindexFilesResult | null = null;
      try {
        rag = await reindexFilesFromContent(owner, repo, files);
        setRagResult(rag);
        onRagFilesUpdated?.(files);
      } catch (ragErr) {
        setRagWarning(ragErr instanceof Error ? ragErr.message : 'Search index update failed');
      }

      const record: RepoPushRecord = {
        branch: result.branch,
        prUrl: result.pr?.prUrl,
        pushedAt: new Date().toISOString(),
        files: editBlock.files.map(f => f.file_path),
        ragUpdated: (rag?.chunksIndexed ?? 0) > 0,
      };
      onPushed?.(record);

      setStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStatus('error');
    } finally {
      setCurrentStep(null);
    }
  }, [
    canSubmit,
    buildBranchOptions,
    willCreateBranch,
    owner,
    repo,
    editBlock,
    onRagFilesUpdated,
    onPushed,
  ]);

  const stepLabel: Record<Step, string> = {
    branch: 'Creating branch…',
    commit: 'Committing files…',
    pr: 'Opening pull request…',
    rag: 'Updating search index…',
  };

  const completedSteps: Step[] = currentStep
    ? activeSteps.slice(0, activeSteps.indexOf(currentStep))
    : [];

  const displayBranch = committedBranch ?? pushRecord?.branch ?? headBranch;
  const displayPrUrl = prUrl ?? pushRecord?.prUrl ?? null;
  const displayRagPaths = ragResult?.paths ?? pushRecord?.files ?? [];
  const displayRagUpdated = (ragResult?.chunksIndexed ?? 0) > 0 || pushRecord?.ragUpdated;

  const selectClass =
    'flex-1 text-sm bg-gray-900/60 border border-purple-500/30 rounded-lg px-3 py-1.5 text-purple-100 focus:outline-none focus:border-purple-400 font-mono disabled:opacity-50';

  return (
    <div className="mt-3 rounded-xl border border-purple-500/30 bg-[#0f0a1e]/80 backdrop-blur-sm overflow-hidden shadow-xl">
      <div className="flex items-center justify-between px-4 py-3 border-b border-purple-500/20 bg-purple-900/20">
        <div className="flex items-center gap-2 text-purple-300 text-sm font-semibold">
          <GitBranch className="w-4 h-4" />
          <span>AI Code Changes</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-500/20 text-xs text-purple-300 font-mono">
            {editBlock.files.length} {editBlock.files.length === 1 ? 'file' : 'files'}
          </span>
          {isPushed && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-500/20 text-xs text-green-400 font-medium">
              Pushed
            </span>
          )}
          <span className="text-xs text-purple-400/60 font-normal font-mono">{owner}/{repo}</span>
        </div>
        {!isPushed && (
        <button
          onClick={onDismiss}
          className="text-gray-500 hover:text-gray-300 transition-colors p-1 rounded"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        )}
      </div>

      <div className="divide-y divide-purple-500/10">
        {editBlock.files.map((file) => {
          const isExpanded = expandedFiles.has(file.file_path);
          const lang = getLanguage(file.file_path);
          const lines = file.content.split('\n').length;

          return (
            <div key={file.file_path}>
              <button
                onClick={() => toggleFile(file.file_path)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-900/20 transition-colors text-left"
              >
                {isExpanded
                  ? <ChevronDown className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                <FileCode className="w-4 h-4 text-purple-300 flex-shrink-0" />
                <span className="text-sm text-purple-100 font-mono flex-1 truncate">{file.file_path}</span>
                <span className="text-xs text-gray-500 flex-shrink-0 font-mono">{lines} lines</span>
                <span className="text-xs text-purple-400/60 flex-shrink-0 ml-2 px-1.5 py-0.5 rounded bg-purple-500/10">{lang}</span>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-2">
                  {file.description && (
                    <p className="text-xs text-purple-300/70 italic border-l-2 border-purple-500/30 pl-2">
                      {file.description}
                    </p>
                  )}
                  <CodeBlock
                    code={`// File: ${file.file_path}\n${file.content}`}
                    language={lang}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isPushed && (
        <div className="px-4 py-3 border-t border-purple-500/20 bg-purple-900/10 space-y-3">
          {/* Push mode */}
          <div className="space-y-1">
            <label className="text-xs text-purple-400/80 font-medium">Push mode</label>
            <select
              value={pushMode}
              onChange={e => setPushMode(e.target.value as PushMode)}
              disabled={status === 'loading'}
              className={selectClass}
            >
              {(Object.keys(PUSH_MODE_LABELS) as PushMode[]).map(mode => (
                <option key={mode} value={mode}>{PUSH_MODE_LABELS[mode]}</option>
              ))}
            </select>
          </div>

          {branchesError && (
            <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Branches unavailable: {branchesError}. You can still type branch names manually.</span>
            </div>
          )}

          {/* new_branch_pr */}
          {pushMode === 'new_branch_pr' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">Base branch</span>
                <select
                  value={baseBranch}
                  onChange={e => setBaseBranch(e.target.value)}
                  disabled={status === 'loading' || branchesLoading}
                  className={selectClass}
                >
                  {branchNames.length > 0
                    ? branchNames.map(name => <option key={name} value={name}>{name}</option>)
                    : <option value={resolvedDefaultBranch}>{resolvedDefaultBranch}</option>}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">New branch</span>
                <input
                  type="text"
                  value={branchName}
                  onChange={e => setBranchName(e.target.value)}
                  disabled={status === 'loading'}
                  placeholder="Branch name…"
                  className="flex-1 text-sm bg-gray-900/60 border border-purple-500/30 rounded-lg px-3 py-1.5 text-purple-100 placeholder-gray-500 focus:outline-none focus:border-purple-400 font-mono disabled:opacity-50"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">PR target</span>
                <select
                  value={prBase}
                  onChange={e => setPrBase(e.target.value)}
                  disabled={status === 'loading' || branchesLoading}
                  className={selectClass}
                >
                  {branchNames.length > 0
                    ? branchNames.map(name => <option key={name} value={name}>{name}</option>)
                    : <option value={resolvedDefaultBranch}>{resolvedDefaultBranch}</option>}
                </select>
              </div>
            </div>
          )}

          {/* existing_branch */}
          {pushMode === 'existing_branch' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">Branch</span>
                <select
                  value={existingHeadBranch}
                  onChange={e => setExistingHeadBranch(e.target.value)}
                  disabled={status === 'loading' || branchesLoading}
                  className={selectClass}
                >
                  {branchNames.length > 0
                    ? branchNames.map(name => <option key={name} value={name}>{name}</option>)
                    : <option value={resolvedDefaultBranch}>{resolvedDefaultBranch}</option>}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-purple-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={openPr}
                  onChange={e => setOpenPr(e.target.checked)}
                  disabled={status === 'loading' || existingHeadBranch === prBase}
                  className="rounded border-purple-500/40"
                />
                Open pull request after commit
              </label>
              {openPr && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">PR target</span>
                  <select
                    value={prBase}
                    onChange={e => setPrBase(e.target.value)}
                    disabled={status === 'loading' || branchesLoading || existingHeadBranch === prBase}
                    className={selectClass}
                  >
                    {branchNames.length > 0
                      ? branchNames.map(name => <option key={name} value={name}>{name}</option>)
                      : <option value={resolvedDefaultBranch}>{resolvedDefaultBranch}</option>}
                  </select>
                </div>
              )}
              {existingHeadBranch === prBase && openPr && (
                <p className="text-xs text-gray-500">PR target must differ from the commit branch.</p>
              )}
            </div>
          )}

          {/* commit_only */}
          {pushMode === 'commit_only' && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs text-purple-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={commitOnlyNewBranch}
                  onChange={e => setCommitOnlyNewBranch(e.target.checked)}
                  disabled={status === 'loading'}
                  className="rounded border-purple-500/40"
                />
                Create new branch (instead of existing)
              </label>
              {commitOnlyNewBranch ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">Base branch</span>
                    <select
                      value={baseBranch}
                      onChange={e => setBaseBranch(e.target.value)}
                      disabled={status === 'loading' || branchesLoading}
                      className={selectClass}
                    >
                      {branchNames.length > 0
                        ? branchNames.map(name => <option key={name} value={name}>{name}</option>)
                        : <option value={resolvedDefaultBranch}>{resolvedDefaultBranch}</option>}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">New branch</span>
                    <input
                      type="text"
                      value={branchName}
                      onChange={e => setBranchName(e.target.value)}
                      disabled={status === 'loading'}
                      placeholder="Branch name…"
                      className="flex-1 text-sm bg-gray-900/60 border border-purple-500/30 rounded-lg px-3 py-1.5 text-purple-100 placeholder-gray-500 focus:outline-none focus:border-purple-400 font-mono disabled:opacity-50"
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-400/80 w-24 flex-shrink-0">Branch</span>
                  <select
                    value={existingHeadBranch}
                    onChange={e => setExistingHeadBranch(e.target.value)}
                    disabled={status === 'loading' || branchesLoading}
                    className={selectClass}
                  >
                    {branchNames.length > 0
                      ? branchNames.map(name => <option key={name} value={name}>{name}</option>)
                      : <option value={resolvedDefaultBranch}>{resolvedDefaultBranch}</option>}
                  </select>
                </div>
              )}
            </div>
          )}

          {status === 'loading' && (
            <div className="flex items-center gap-3 text-xs flex-wrap">
              {activeSteps.map((step) => {
                const isDone = completedSteps.includes(step);
                const isActive = currentStep === step;
                return (
                  <div
                    key={step}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${
                      isDone
                        ? 'border-green-500/40 text-green-400 bg-green-500/10'
                        : isActive
                          ? 'border-purple-400/60 text-purple-300 bg-purple-500/20'
                          : 'border-gray-600/40 text-gray-500'
                    }`}
                  >
                    {isDone ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : isActive ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-current" />
                    )}
                    <span>{stepLabel[step].replace('…', '')}</span>
                  </div>
                );
              })}
            </div>
          )}

          {status === 'error' && error && (
            <div className="flex items-start gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">
              <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleCommit}
              disabled={status === 'loading' || !canSubmit}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors shadow"
            >
              {status === 'loading'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : pushMode === 'commit_only'
                  ? <GitCommit className="w-4 h-4" />
                  : <GitPullRequest className="w-4 h-4" />}
              {status === 'loading' ? 'Working…' : primaryButtonLabel}
            </button>
            <button
              onClick={onDismiss}
              disabled={status === 'loading'}
              className="px-4 py-2 rounded-lg border border-gray-600 hover:border-gray-400 text-gray-400 hover:text-gray-200 text-sm transition-colors disabled:opacity-50"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {isPushed && (
        <div className="px-4 py-4 border-t border-green-500/20 bg-green-950/20 space-y-3">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold text-sm">
              {displayPrUrl ? 'Pushed — Pull Request open' : 'Pushed to GitHub'}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {displayPrUrl && (
              <a
                href={displayPrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-700/30 hover:bg-green-600/30 text-green-300 text-sm font-medium transition-colors border border-green-600/30"
              >
                <GitPullRequest className="w-4 h-4" />
                View Pull Request on GitHub
                <ExternalLink className="w-3 h-3 opacity-70" />
              </a>
            )}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 font-mono">
              <GitCommit className="w-3.5 h-3.5" />
              {displayBranch}
            </div>
          </div>

          {displayRagUpdated && displayRagPaths.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-green-300/90 bg-green-500/10 border border-green-500/20 rounded-lg p-2">
              <Database className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Search index updated for {displayRagPaths.length} file
                {displayRagPaths.length === 1 ? '' : 's'}:{' '}
                {displayRagPaths.map(p => `\`${p}\``).join(', ')}
              </span>
            </div>
          )}

          {ragWarning && (
            <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Push succeeded but search index update failed: {ragWarning}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
